import React, { useState, useEffect } from "react";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import TokenChart from "../components/token/TokenChart";
import TokenChartV2 from "../components/token/TokenChartV2";
import TokenInfo from "../components/token/TokenInfo";
import TokenTabs from "../components/token/TokenTabs";
import TokenTransactions from "../components/token/TokenTransactions";
import TokenHolders from "../components/token/TokenHolders";
import TokenSnipers from "../components/token/TokenSnipers";
import TokenHolderInsights from "../components/token/TokenHolderInsights";
import PulseTokensSidebar from "../components/token/PulseTokensSidebar";
import PortfolioPanel from "../components/token/PortfolioPanel";
import TransparentHeader from "../components/layout/TransparentHeader";
import { getTokenPairs, getPairByAddress } from "../services/dexscreener-api";

const API_KEY = process.env.REACT_APP_MORALIS_API_KEY;

// Feature flags to disable Moralis API calls
const USE_DEXSCREENER_PAIRS_LIST = process.env.REACT_APP_USE_DEXSCREENER_PAIRS_LIST === "true";
const USE_DEXSCREENER_TOKEN_METADATA = process.env.REACT_APP_USE_DEXSCREENER_TOKEN_METADATA === "true";

// Map URL path segments to Moralis chain IDs
const PATH_TO_CHAIN_ID = {
  ethereum: "0x1",
  binance: "0x38",
  bsc: "0x38",
  polygon: "0x89",
  solana: "solana",
  arbitrum: "0xa4b1",
  base: "0x2105",
  avalanche: "0xa86a",
  optimism: "0xa",
  linea: "0xe708",
  fantom: "0xfa",
  pulse: "0x171",
  ronin: "0x7e4",
};

const TokenPage = () => {
  const { chainId: chainPath, tokenAddress } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loadingState, setLoadingState] = useState("initial"); // 'initial', 'data', 'complete'
  const [tokenInfo, setTokenInfo] = useState(null);
  const [, setPairs] = useState([]); // pairs state used internally via setPairs
  const [selectedPair, setSelectedPair] = useState(null);
  const [activeTab, setActiveTab] = useState("snipers");
  const [timeFrame, setTimeFrame] = useState("1m"); // Default to 1 minute
  const [error, setError] = useState(null);
  const [bottomSectionExpanded, setBottomSectionExpanded] = useState(false);
  
  // Get chainId from URL params or default to solana
  const urlChainId = searchParams.get("chain") || "solana";

  // Convert URL path parameter to chain ID for API
  const getApiChainId = (chainPath) => {
    const chainMap = {
      ethereum: "0x1",
      binance: "0x38",
      bsc: "0x38",
      polygon: "0x89",
      solana: "solana",
      arbitrum: "0xa4b1",
      base: "0x2105",
      avalanche: "0xa86a",
      optimism: "0xa",
      linea: "0xe708",
      fantom: "0xfa",
      pulse: "0x171",
      ronin: "0x7e4",
    };

    return chainMap[chainPath] || chainPath;
  };

  const chainId = getApiChainId(chainPath);
  const isSolana = chainId === "solana";
  
  // Convert chainId to DexScreener format for sidebar
  const getDexScreenerChainId = (apiChainId) => {
    if (apiChainId === "solana") return "solana";
    if (apiChainId === "0x1") return "ethereum";
    if (apiChainId === "0x38") return "bsc";
    if (apiChainId === "0x89") return "polygon";
    if (apiChainId === "0xa4b1") return "arbitrum";
    if (apiChainId === "0x2105") return "base";
    if (apiChainId === "0xa") return "optimism";
    if (apiChainId === "0xa86a") return "avalanche";
    // Fallback to chainPath or urlChainId
    return chainPath || urlChainId || "solana";
  };
  
  const sidebarChainId = getDexScreenerChainId(chainId);

  useEffect(() => {
    // Reset loading state and error when navigating to a new token
    setLoadingState("initial");
    setError(null); // Clear any previous errors

    console.log("TokenPage mounted with params:", {
      chainPath,
      tokenAddress,
      chainId: getApiChainId(chainPath),
    });

    // Check if API_KEY is available (don't log the actual key)
    console.log("API key available:", !!API_KEY);

    // Minimum loading time to prevent flashing
    const minLoadTimer = setTimeout(() => {
      setLoadingState("data");
    }, 300);

    return () => {
      console.log("TokenPage unmounted");
      clearTimeout(minLoadTimer);
    };
  }, [chainPath, tokenAddress]);

  // Check if token data and pairAddress were passed via navigation state
  useEffect(() => {
    const tokenDataFromState = location.state?.tokenData;
    const pairAddressFromState = location.state?.pairAddress;
    
    if (tokenDataFromState) {
      setTokenInfo(tokenDataFromState);
    }
    
    // If we have pairAddress from navigation state, create minimal pair immediately so chart can render
    if (pairAddressFromState) {
      console.log("📊 [TokenPage] Creating minimal pair from navigation state immediately for chart:", pairAddressFromState);
      const minimalPair = {
        chainId: chainId,
        pairAddress: pairAddressFromState,
        exchangeName: "Unknown DEX",
        exchangeLogo: null,
        pairLabel: "Loading...",
        liquidityUsd: 0,
        usdPrice: 0,
        usdPrice24hrPercentChange: 0,
        volume24hrUsd: 0,
        baseToken: null,
        quoteToken: null,
        pair: [],
      };
      setSelectedPair(minimalPair);
      console.log("📊 [TokenPage] Set minimal selectedPair from navigation state:", {
        pairAddress: minimalPair.pairAddress,
        chainId: minimalPair.chainId
      });
    }
  }, [location, chainId]);

  // Fetch token pairs
  useEffect(() => {
    const fetchTokenPairs = async () => {
      // Use DexScreener API if feature flag is enabled
      console.log("🔍 [DEBUG] USE_DEXSCREENER_PAIRS_LIST:", USE_DEXSCREENER_PAIRS_LIST);
      console.log("🔍 [DEBUG] process.env.REACT_APP_USE_DEXSCREENER_PAIRS_LIST:", process.env.REACT_APP_USE_DEXSCREENER_PAIRS_LIST);
      
      if (USE_DEXSCREENER_PAIRS_LIST) {
        console.log("📊 Fetching pairs from DexScreener API");
        setError(null);
        setLoadingState("data");
        
        try {
          const cleanTokenAddress = tokenAddress.trim();
          const dexScreenerChainId = getDexScreenerChainId(chainId);
          const pairAddressFromState = location.state?.pairAddress;
          
          // If we have pairAddress from navigation state, try fetching pair directly first
          if (pairAddressFromState) {
            console.log(`📊 [DexScreener] Attempting to fetch pair by address: ${pairAddressFromState}`);
            try {
              const pairData = await getPairByAddress(dexScreenerChainId, pairAddressFromState);
              
              if (pairData && pairData.pairs && pairData.pairs.length > 0) {
                const pair = pairData.pairs[0];
                // Normalize to our format
                const normalizedPair = {
                  chainId: chainId,
                  pairAddress: pair.pairAddress,
                  exchangeName: pair.dexId || "Unknown DEX",
                  exchangeLogo: null,
                  pairLabel: pair.baseToken?.symbol && pair.quoteToken?.symbol 
                    ? `${pair.baseToken.symbol}/${pair.quoteToken.symbol}`
                    : "Unknown Pair",
                  liquidityUsd: pair.liquidity?.usd || 0,
                  usdPrice: parseFloat(pair.priceUsd) || 0,
                  usdPrice24hrPercentChange: pair.priceChange?.h24 || 0,
                  volume24hrUsd: pair.volume?.h24 || 0,
                  baseToken: pair.baseToken,
                  quoteToken: pair.quoteToken,
                  pair: [
                    {
                      tokenAddress: pair.baseToken?.address,
                      tokenName: pair.baseToken?.name,
                      tokenSymbol: pair.baseToken?.symbol,
                      tokenLogo: pair.info?.imageUrl || null,
                      tokenDecimals: null,
                      pairTokenType: "token0",
                      liquidityUsd: pair.liquidity?.base || 0,
                    },
                    {
                      tokenAddress: pair.quoteToken?.address,
                      tokenName: pair.quoteToken?.name,
                      tokenSymbol: pair.quoteToken?.symbol,
                      tokenLogo: null,
                      tokenDecimals: null,
                      pairTokenType: "token1",
                      liquidityUsd: pair.liquidity?.quote || 0,
                    },
                  ],
                };
                
                console.log("✅ Successfully fetched pair using pairAddress from state");
                console.log("📊 [TokenPage] Setting selectedPair from navigation state:", {
                  pairAddress: normalizedPair?.pairAddress,
                  chainId: normalizedPair?.chainId,
                  pair: normalizedPair
                });
                setPairs([normalizedPair]);
                setSelectedPair(normalizedPair);
                
                // Set token info
                const currentToken = normalizedPair.pair.find(
                  (token) =>
                    token.tokenAddress &&
                    token.tokenAddress.toLowerCase() === cleanTokenAddress.toLowerCase()
                ) || normalizedPair.pair[0];
                
                setTokenInfo({
                  address: cleanTokenAddress,
                  name: currentToken?.tokenName || tokenInfo?.name || `Token ${cleanTokenAddress.substring(0, 6)}...`,
                  symbol: currentToken?.tokenSymbol || tokenInfo?.symbol || "TOKEN",
                  logo: currentToken?.tokenLogo || tokenInfo?.logo || null,
                  decimals: currentToken?.tokenDecimals || "18",
                });
                
                setLoadingState("complete");
                return; // Success! Exit early
              }
            } catch (pairError) {
              console.warn("⚠️ [DexScreener] Failed to fetch pair by address, falling back to token pairs:", pairError);
              // Continue to try fetching by token address
            }
          }
          
          console.log(`📊 [DexScreener] Fetching pairs for token:`, {
            tokenAddress: cleanTokenAddress,
            chainId: chainId,
            dexScreenerChainId: dexScreenerChainId,
            url: `https://api.dexscreener.com/token-pairs/v1/${dexScreenerChainId}/${cleanTokenAddress}`
          });
          
          // Fetch pairs from DexScreener
          const pairs = await getTokenPairs(dexScreenerChainId, cleanTokenAddress);
          
          console.log(`📊 [DexScreener] API Response:`, {
            pairsCount: pairs?.length || 0,
            pairs: pairs,
            isArray: Array.isArray(pairs),
            firstPairStructure: pairs && pairs.length > 0 ? Object.keys(pairs[0]) : null
          });
          
          if (pairs && pairs.length > 0) {
            // Normalize DexScreener pairs to our format
            const normalizedPairs = pairs.map((pair, index) => {
              // DexScreener API returns pairAddress directly in the pair object
              const pairAddress = pair.pairAddress || null;
              
              if (!pairAddress) {
                console.warn(`⚠️ [TokenPage] Pair at index ${index} has no pairAddress:`, {
                  pair: pair,
                  pairKeys: Object.keys(pair || {})
                });
              }
              
              return {
                chainId: pair.chainId || chainId, // Use DexScreener's chainId if available (already in DexScreener format), fallback to our internal chainId
                pairAddress: pairAddress, // Critical for GeckoTerminal chart
                exchangeName: pair.dexId || "Unknown DEX",
                exchangeLogo: null,
                pairLabel: pair.baseToken?.symbol && pair.quoteToken?.symbol 
                  ? `${pair.baseToken.symbol}/${pair.quoteToken.symbol}`
                  : "Unknown Pair",
                liquidityUsd: pair.liquidity?.usd || 0,
                usdPrice: parseFloat(pair.priceUsd || 0),
                usdPrice24hrPercentChange: pair.priceChange?.h24 || 0,
                volume24hrUsd: pair.volume?.h24 || 0,
                baseToken: pair.baseToken,
                quoteToken: pair.quoteToken,
                pair: [
                  {
                    tokenAddress: pair.baseToken?.address,
                    tokenName: pair.baseToken?.name,
                    tokenSymbol: pair.baseToken?.symbol,
                    tokenLogo: pair.info?.imageUrl || null,
                    tokenDecimals: null,
                    pairTokenType: "token0",
                    liquidityUsd: pair.liquidity?.base || 0,
                  },
                  {
                    tokenAddress: pair.quoteToken?.address,
                    tokenName: pair.quoteToken?.name,
                    tokenSymbol: pair.quoteToken?.symbol,
                    tokenLogo: null,
                    tokenDecimals: null,
                    pairTokenType: "token1",
                    liquidityUsd: pair.liquidity?.quote || 0,
                  },
                ],
              };
            }).filter(p => p.pairAddress); // Only include pairs that have a pairAddress (required for chart)

            // Sort by liquidity (highest first)
            normalizedPairs.sort((a, b) => (b.liquidityUsd || 0) - (a.liquidityUsd || 0));

            console.log(`✅ Found ${normalizedPairs.length} pairs from DexScreener`);
            
            if (normalizedPairs.length === 0) {
              console.error("❌ [TokenPage] No pairs with pairAddress found after normalization!");
              setPairs([]);
              setSelectedPair(null);
            } else {
              setPairs(normalizedPairs);
              const firstPair = normalizedPairs[0];
              console.log("📊 [TokenPage] Setting selectedPair (will enable GeckoTerminal chart):", {
                pairAddress: firstPair?.pairAddress,
                chainId: firstPair?.chainId,
                hasPairAddress: !!firstPair?.pairAddress,
                pair: firstPair
              });
              setSelectedPair(firstPair);
            }

            // Extract token info from the first pair
            if (normalizedPairs[0] && normalizedPairs[0].pair && normalizedPairs[0].pair.length > 0) {
              const currentToken = normalizedPairs[0].pair.find(
                (token) =>
                  token.tokenAddress &&
                  token.tokenAddress.toLowerCase() === cleanTokenAddress.toLowerCase()
              );

              if (currentToken) {
                setTokenInfo({
                  address: cleanTokenAddress,
                  name: currentToken.tokenName || `Token ${cleanTokenAddress.substring(0, 6)}...`,
                  symbol: currentToken.tokenSymbol || "TOKEN",
                  logo: currentToken.tokenLogo || null,
                  decimals: currentToken.tokenDecimals || "18",
                });
              } else {
                // Fallback to first token in pair
                const fallbackToken = normalizedPairs[0].pair[0];
                setTokenInfo({
                  address: cleanTokenAddress,
                  name: fallbackToken?.tokenName || `Token ${cleanTokenAddress.substring(0, 6)}...`,
                  symbol: fallbackToken?.tokenSymbol || "TOKEN",
                  logo: fallbackToken?.tokenLogo || null,
                  decimals: fallbackToken?.tokenDecimals || "18",
                });
              }
            }

            console.log("📊 [TokenPage] Setting loadingState to 'complete' (pairs found)");
            setLoadingState("complete");
          } else {
            // No pairs found from token-pairs endpoint
            // Try to get pair address from the first pair in the response (sometimes it's nested)
            console.warn("⚠️ [DexScreener] No trading pairs returned (empty array):", {
              tokenAddress: cleanTokenAddress,
              chainId: chainId,
              dexScreenerChainId: dexScreenerChainId,
              responseWasArray: Array.isArray(pairs),
              responseValue: pairs
            });
            
            // Check if we have pairAddress from navigation state
            const pairAddressFromState = location.state?.pairAddress;
            
            // If no pairs but we have pairAddress from state, use it
            if (pairAddressFromState) {
              console.log("📊 [TokenPage] Creating minimal pair from navigation state for GeckoTerminal chart");
              const minimalPair = {
                chainId: chainId,
                pairAddress: pairAddressFromState,
                exchangeName: "Unknown DEX",
                exchangeLogo: null,
                pairLabel: "Unknown Pair",
                liquidityUsd: 0,
                usdPrice: 0,
                usdPrice24hrPercentChange: 0,
                volume24hrUsd: 0,
                baseToken: null,
                quoteToken: null,
                pair: [
                  {
                    tokenAddress: cleanTokenAddress,
                    tokenName: tokenInfo?.name || `Token ${cleanTokenAddress.substring(0, 6)}...`,
                    tokenSymbol: tokenInfo?.symbol || "TOKEN",
                    tokenLogo: tokenInfo?.logo || null,
                    tokenDecimals: tokenInfo?.decimals || "18",
                    pairTokenType: "token0",
                    liquidityUsd: 0,
                  },
                ],
              };
              console.log("📊 [TokenPage] Setting selectedPair (minimal from state):", {
                pairAddress: minimalPair.pairAddress,
                chainId: minimalPair.chainId
              });
              setPairs([]);
              setSelectedPair(minimalPair);
            } else {
              // No pairAddress - try to fetch it again or show error
              console.warn("⚠️ [TokenPage] No pairAddress available - chart cannot load");
              setPairs([]);
              setSelectedPair(null);
            }
            
            // Set basic token info
            if (!tokenInfo) {
              setTokenInfo({
                address: cleanTokenAddress,
                name: `Token ${cleanTokenAddress.substring(0, 6)}...`,
                symbol: "TOKEN",
                logo: null,
                decimals: "18",
              });
            }
            
            setLoadingState("complete");
          }
        } catch (dexError) {
          console.error("❌ [DexScreener] Error fetching pairs:", {
            error: dexError,
            message: dexError?.message,
            tokenAddress: tokenAddress?.trim(),
            chainId: chainId,
            dexScreenerChainId: getDexScreenerChainId(chainId),
            stack: dexError?.stack,
            note: "This error doesn't mean pairs don't exist - API might be slow or token might be newly listed"
          });
          
          // If it's a 404 or network error, the token might still exist
          // Don't set error for DexScreener failures - just log and continue
          // Don't show "no pairs" message if there was an actual error - error means uncertainty
          setError(null);
          setPairs([]);
          
          // Try to create minimal pair from navigation state if available (for chart to work)
          const pairAddressFromState = location.state?.pairAddress;
          if (pairAddressFromState) {
            console.log("📊 Creating minimal pair object from navigation state (error fallback)");
            const cleanTokenAddress = tokenAddress.trim();
            const minimalPair = {
              chainId: chainId,
              pairAddress: pairAddressFromState,
              exchangeName: "Unknown DEX",
              exchangeLogo: null,
              pairLabel: "Unknown Pair",
              liquidityUsd: 0,
              usdPrice: 0,
              usdPrice24hrPercentChange: 0,
              volume24hrUsd: 0,
              baseToken: null,
              quoteToken: null,
              pair: [
                {
                  tokenAddress: cleanTokenAddress,
                  tokenName: tokenInfo?.name || `Token ${cleanTokenAddress.substring(0, 6)}...`,
                  tokenSymbol: tokenInfo?.symbol || "TOKEN",
                  tokenLogo: tokenInfo?.logo || null,
                  tokenDecimals: tokenInfo?.decimals || "18",
                  pairTokenType: "token0",
                  liquidityUsd: 0,
                },
              ],
            };
            setSelectedPair(minimalPair);
          } else {
            setSelectedPair(null);
          }
          
          // Set basic token info
          if (!tokenInfo) {
            setTokenInfo({
              address: tokenAddress.trim(),
              name: `Token ${tokenAddress.substring(0, 6)}...`,
              symbol: "TOKEN",
              logo: null,
              decimals: "18",
            });
          }
          
          setLoadingState("complete");
        }
        
        return;
      }

      try {
        let url;

        // Clean token address (remove any whitespace or invalid characters)
        const cleanTokenAddress = tokenAddress.trim();

        if (isSolana) {
          // Solana endpoint - validate address format (Base58, pump.fun tokens can be 32-50 chars)
          if (!/^[1-9A-HJ-NP-Za-km-z]{32,50}$/.test(cleanTokenAddress)) {
            throw new Error(`Invalid Solana address format: ${cleanTokenAddress}`);
          }
          url = `https://solana-gateway.moralis.io/token/mainnet/${cleanTokenAddress}/pairs`;
        } else {
          // EVM endpoint - validate address format (0x + 40 hex chars)
          const evmAddress = cleanTokenAddress.startsWith('0x') ? cleanTokenAddress : `0x${cleanTokenAddress}`;
          url = `https://deep-index.moralis.io/api/v2.2/erc20/${evmAddress}/pairs?chain=${chainId}`;
        }

        console.log("Fetching token pairs from URL:", url);
        console.log("Token address:", cleanTokenAddress);

        // Skip API key check if using DexScreener - Moralis is only fallback now
        // But still check if we actually need to call Moralis
        if (!API_KEY) {
          console.warn("⚠️ Moralis API key not configured - skipping Moralis fallback. Using DexScreener only.");
          // Don't throw error - just skip Moralis and rely on DexScreener
          setPairs([]);
          setLoadingState("complete");
          return;
        }

        const response = await fetch(url, {
          headers: {
            accept: "application/json",
            "X-API-Key": API_KEY,
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("API Error Response:", errorText);
          
          // Handle 404 gracefully - token exists but no pairs
          if (response.status === 404) {
            console.warn("No trading pairs found for token - this is normal for new tokens");
            setPairs([]);
            setLoadingState("complete");
            return; // Exit early, don't throw error
          }
          
          // Handle 400 - invalid address
          if (response.status === 400) {
            throw new Error(`Invalid token address: ${cleanTokenAddress}`);
          }
          
          // Handle 401 - API key issue (don't show error since we're using DexScreener as primary)
          if (response.status === 401) {
            console.warn("⚠️ Moralis API key invalid - this is expected if using DexScreener. Ignoring error.");
            setPairs([]);
            setLoadingState("complete");
            return; // Don't throw error - just skip Moralis
          }
          
          // Other errors
          throw new Error(`API error: ${response.status}. ${errorText.substring(0, 100)}`);
        }

        const data = await response.json();
        console.log("Token pairs response:", data);

        // Normalize data from different API formats
        const pairsData = data.pairs || [];

        if (pairsData.length > 0) {
          // Create normalized pairs data with consistent property names
          const normalizedPairs = pairsData.map((pair) => {
            // Handle both snake_case and camelCase formats
            return {
              chainId: chainId,
              pairAddress: pair.pairAddress || pair.pair_address,
              exchangeName: pair.exchangeName || pair.exchange_name,
              exchangeLogo: pair.exchangeLogo || pair.exchange_logo,
              pairLabel: pair.pairLabel || pair.pair_label,
              liquidityUsd: pair.liquidityUsd || pair.liquidity_usd,
              usdPrice: pair.usdPrice || pair.usd_price,
              usdPrice24hrPercentChange:
                pair.usdPrice24hrPercentChange ||
                pair.usd_price_24hr_percent_change,
              volume24hrUsd: pair.volume24hrUsd || pair.volume_24h_usd,
              baseToken: pair.baseToken || pair.base_token,
              quoteToken: pair.quoteToken || pair.quote_token,
              pair: Array.isArray(pair.pair)
                ? pair.pair.map((token) => ({
                    tokenAddress: token.tokenAddress || token.token_address,
                    tokenName: token.tokenName || token.token_name,
                    tokenSymbol: token.tokenSymbol || token.token_symbol,
                    tokenLogo: token.tokenLogo || token.token_logo,
                    tokenDecimals: token.tokenDecimals || token.token_decimals,
                    pairTokenType: token.pairTokenType || token.pair_token_type,
                    liquidityUsd: token.liquidityUsd || token.liquidity_usd,
                  }))
                : [],
            };
          });

          console.log("Normalized pairs data:", normalizedPairs);
          setPairs(normalizedPairs);

          // Select the first pair by default
          setSelectedPair(normalizedPairs[0]);

          // Extract token info from the first pair if we don't have it yet
          if (
            !tokenInfo &&
            normalizedPairs[0].pair &&
            normalizedPairs[0].pair.length > 0
          ) {
            // Try to find the token in the pair data
            const currentToken = normalizedPairs[0].pair.find(
              (token) =>
                token.tokenAddress &&
                token.tokenAddress.toLowerCase() === tokenAddress.toLowerCase()
            );

            if (currentToken) {
              setTokenInfo({
                address: tokenAddress,
                name: currentToken.tokenName,
                symbol: currentToken.tokenSymbol,
                logo: currentToken.tokenLogo,
                decimals: currentToken.tokenDecimals,
              });
            } else {
              // Fallback if token not found in pair data
              console.log(
                "Token not found in pair data, using first token as fallback"
              );
              const fallbackToken = normalizedPairs[0].pair[0];
              setTokenInfo({
                address: tokenAddress,
                name:
                  fallbackToken?.tokenName ||
                  `Token ${tokenAddress.substring(0, 6)}...`,
                symbol: fallbackToken?.tokenSymbol || "TOKEN",
                logo: fallbackToken?.tokenLogo || null,
                decimals: fallbackToken?.tokenDecimals || "18",
              });
            }
          }

          // Set loading to complete once we have the pairs data
          setLoadingState("complete");
        } else {
          // No pairs found - this is normal for new tokens
          console.log("No trading pairs found for this token");
          setPairs([]);
          setError(null); // Don't show error - just show token without pairs
          
          // If we have token info from navigation state, use it
          if (tokenInfo && tokenInfo.address) {
            console.log("Using token info from navigation state");
          } else {
            // Skip Moralis metadata fetch if feature flag is enabled
            if (USE_DEXSCREENER_TOKEN_METADATA) {
              console.log("🚫 Skipping Moralis metadata fetch - USE_DEXSCREENER_TOKEN_METADATA is enabled");
              // TODO: Implement DexScreener metadata fetch here
            } else {
              // Try to fetch metadata as fallback (only if API key is available)
              if (!API_KEY) {
                console.warn("⚠️ Moralis API key not configured - skipping metadata fetch. Using DexScreener only.");
              } else {
                try {
                  const metadataUrl = isSolana
                    ? `https://solana-gateway.moralis.io/token/mainnet/${cleanTokenAddress}/metadata`
                    : `https://deep-index.moralis.io/api/v2.2/erc20/metadata?chain=${chainId}&addresses[0]=${cleanTokenAddress}`;
                  
                  const metadataResponse = await fetch(metadataUrl, {
                    headers: {
                      accept: "application/json",
                      "X-API-Key": API_KEY,
                    },
                  });

                  // Handle 401 gracefully (don't throw error)
                  if (metadataResponse.status === 401) {
                    console.warn("⚠️ Moralis API key invalid - skipping metadata fetch. Using DexScreener only.");
                  } else if (metadataResponse.ok) {
                    const metadata = await metadataResponse.json();
                    const fetchedMetadata = isSolana
                      ? metadata
                      : (Array.isArray(metadata) && metadata.length > 0 ? metadata[0] : null);

                    if (fetchedMetadata) {
                      setTokenInfo({
                        address: cleanTokenAddress,
                        name: fetchedMetadata.name || fetchedMetadata.tokenName || `Token ${cleanTokenAddress.substring(0, 6)}...`,
                        symbol: fetchedMetadata.symbol || fetchedMetadata.tokenSymbol || "TOKEN",
                        logo: fetchedMetadata.logo || fetchedMetadata.logoURI || null,
                        decimals: fetchedMetadata.decimals || fetchedMetadata.tokenDecimals || "18",
                      });
                    }
                  }
                } catch (metadataError) {
                  console.warn("Could not fetch metadata:", metadataError);
                  // Still set basic token info
                  setTokenInfo({
                    address: cleanTokenAddress,
                    name: `Token ${cleanTokenAddress.substring(0, 6)}...`,
                    symbol: "TOKEN",
                    logo: null,
                    decimals: "18",
                  });
                }
              }
            }
          }
          setLoadingState("complete");
        }
      } catch (error) {
        console.error("Error fetching token pairs:", error);
        // Filter out API key errors and rate limit errors since we're using DexScreener
        const errorMessage = error.message || "";
        if (
          errorMessage.includes("429") || 
          errorMessage.includes("Rate limit") ||
          errorMessage.includes("API key") ||
          errorMessage.includes("Invalid API key") ||
          errorMessage.includes("not configured")
        ) {
          console.warn("⚠️ Ignoring Moralis API error - using DexScreener only:", errorMessage);
          setError(null); // Clear error for API key/rate limit issues
        } else {
          // Only show non-API-key related errors
          setError(`Error loading token data: ${errorMessage}`);
        }
        setLoadingState("complete");
      }
    };

    if (tokenAddress && chainId && loadingState === "data") {
      fetchTokenPairs();
    }
  }, [chainId, tokenAddress, tokenInfo, isSolana, loadingState, location.state]);

  // Handle time frame change
  const handleTimeFrameChange = (newTimeFrame) => {
    setTimeFrame(newTimeFrame);
  };

  // Update activeTab if it's set to "holders" for Solana tokens (default to snipers)
  useEffect(() => {
    if (isSolana && activeTab === "holders") {
      setActiveTab("snipers");
    }
  }, [isSolana, activeTab]);

  // Loading spinner - consistent for all loading states except 'complete'
  if (loadingState !== "complete") {
    return (
      <div className="relative min-h-screen w-full overflow-x-hidden">
        {/* Background Image with Dim Overlay */}
        <div 
          className="fixed inset-0 z-0"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL || ''}/background-image.png)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed'
          }}
        />
        <div className="fixed inset-0 z-0 bg-[#0a0e1a]/70" />
        <div className="fixed inset-0 z-0 bg-[#0d4d4d]/20" />
        
        <TransparentHeader />
        
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
            <div className="text-xl text-white font-inter">
              Loading token data...
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error only for actual errors, not for missing pairs or rate limits
  if (error && !error.includes("No trading pairs") && !error.includes("429") && !error.includes("Rate limit")) {
    return (
      <div className="relative min-h-screen w-full overflow-x-hidden">
        {/* Background Image with Dim Overlay */}
        <div 
          className="fixed inset-0 z-0"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL || ''}/background-image.png)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed'
          }}
        />
        <div className="fixed inset-0 z-0 bg-[#0a0e1a]/70" />
        <div className="fixed inset-0 z-0 bg-[#0d4d4d]/20" />
        
        <TransparentHeader />
        
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md mx-auto px-4">
            <div className="text-4xl mb-4">⚠️</div>
            <div className="text-xl text-white mb-4 font-inter">{error}</div>
            {error.includes("API key") && (
              <div className="text-sm text-white/70 font-inter">
                Please check your .env file and ensure REACT_APP_MORALIS_API_KEY is set correctly.
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden max-w-full">
      {/* Background Image with Dim Overlay */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL || ''}/background-image.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      />
      <div className="fixed inset-0 z-0 bg-[#0a0e1a]/70" />
      <div className="fixed inset-0 z-0 bg-[#0d4d4d]/20" />
      
      <TransparentHeader />
      
      <div className="relative z-10 flex flex-col h-screen overflow-hidden pt-16 lg:pt-20 w-full">
        <div className={`flex flex-row flex-1 min-h-0 transition-all duration-300 w-full ${
          bottomSectionExpanded ? 'h-[60vh]' : 'h-full'
        }`}>
          {/* Left sidebar - Pulse tokens */}
          <div className="w-64 xl:w-80 border-r border-white/20 overflow-y-auto bg-white/5 backdrop-blur-sm">
            <PulseTokensSidebar 
              currentTokenAddress={tokenAddress}
              chainId={sidebarChainId}
            />
          </div>

          {/* Center - Chart section (takes most space) */}
          <div className="flex-1 p-4 lg:p-6 min-h-0">
            {/* Always render GeckoTerminal chart - it handles its own loading state */}
            <TokenChartV2
              pair={selectedPair}
              timeFrame={timeFrame}
              onTimeFrameChange={handleTimeFrameChange}
              tokenAddress={tokenAddress}
              chainId={chainId}
            />
          </div>

          {/* Right side - Token info section (compact sidebar) */}
          <div className="w-full lg:w-80 xl:w-96 border-l border-white/20 overflow-y-auto bg-white/5 backdrop-blur-sm">
            <TokenInfo
              token={tokenInfo}
              pair={selectedPair}
              timeFrame={timeFrame}
              chainId={chainId}
              onPairStatsUpdate={(updatedPair) => {
                // Update selectedPair with fresh stats data
                setSelectedPair(updatedPair);
                // Also update in pairs array to keep it in sync
                setPairs(prevPairs => 
                  prevPairs.map(p => 
                    p.pairAddress === updatedPair.pairAddress ? updatedPair : p
                  )
                );
              }}
            />
            
            {/* Portfolio Panel - Only for Solana */}
            {chainId === "solana" && (
              <div className="border-t border-white/20">
                <PortfolioPanel />
              </div>
            )}
            
            {/* Removed "no pairs" message - it was showing incorrectly when pairs do exist */}
            {/* The chart will load automatically when data is available */}
          </div>
        </div>

        {/* Collapsible bottom section */}
        <div className={`flex flex-col border-t border-white/20 bg-white/5 backdrop-blur-sm transition-all duration-300 ${
          bottomSectionExpanded ? 'h-[40vh]' : 'h-auto'
        }`}>
          {/* Tabs section with expand/collapse button */}
          <div className="flex items-center justify-between border-b border-white/20">
            <TokenTabs
              activeTab={activeTab}
              onChange={(tab) => {
                setActiveTab(tab);
                setBottomSectionExpanded(true); // Auto-expand when clicking a tab
              }}
              isSolana={isSolana}
            />
            <button
              onClick={() => setBottomSectionExpanded(!bottomSectionExpanded)}
              className="flex items-center gap-2 px-6 py-3 hover:bg-white/10 transition-colors text-white/70 hover:text-white"
            >
              <span className="text-xs font-inter">
                {bottomSectionExpanded ? 'Collapse' : 'Expand'}
              </span>
              <svg
                className={`w-4 h-4 transition-transform duration-300 ${
                  bottomSectionExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Tab content - only show when expanded */}
          {bottomSectionExpanded && (
            <div className="flex-1 overflow-auto bg-white/5 backdrop-blur-sm min-h-0">
              {/* Only render TokenTransactions when transactions tab is active */}
              {activeTab === "transactions" && (
                <TokenTransactions
                  pair={selectedPair}
                  chainId={selectedPair?.chainId}
                />
              )}

              {activeTab === "holders" && (
                <TokenHolders token={tokenInfo} chainId={chainId} />
              )}

              {activeTab === "holder-insights" && (
                <TokenHolderInsights token={tokenInfo} chainId={chainId} />
              )}

              {activeTab === "snipers" && (
                <TokenSnipers
                  token={tokenInfo}
                  pair={selectedPair}
                  chainId={chainId}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenPage;
